import * as Actions from '../actions/groupActions';
import Group from '../../entities/group';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type GroupAction = { 
    type: string; 
    group: Group | null; 
    groupId: string | Group; 
    partial: boolean; 
    lastFetch: number; 
    groups: Group[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Group> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Group[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Group> | null, firmId: string, userId: string, updates?: Group[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'group', 'groups', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Group>} data
 * @param {Group[]} [updates]
 * @returns {ArrayMap<Group>}
 */
function updateStorage(data: ArrayMap<Group>, updates?: Group[]): ArrayMap<Group> {
	if (logging) console.debug('⮂ groupReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Group[]} [updates]
 * @returns {ArrayMap<Group>}
 */
function updateStorageNoReturn(updates?: Group[]): void {
	if (logging) console.debug('⮂ groupReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Group[]} [data]
 * @returns {ArrayMap<Group>}
 */
function createState(data?: Group[] | ArrayMap<Group>): ArrayMap<Group> {
	if (!data) return ArrayMapRegistry.clone<Group>('group', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Group>('group', data.map(e => e));
    else return ArrayMapRegistry.clone<Group>('group', data);
}

function groups(state: ArrayMap<Group>, action: GroupAction): ArrayMap<Group> {
	if (!state) state = createState();

	let st: ArrayMap<Group>;

	switch (action.type) {
		// action.group - the new value of group with id action.id
		// action.groupId - id of the target, used if new value is null
		case Actions.GROUP_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ groupReducer: FETCH_ONE_SUCCESS', action);

			if (action.group === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.groupId)) {
				st = state.clone();
				st.set(action.group);
				// state.clear();

				updateStorageNoReturn([action.group]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.groups
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.groups
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.groups
			 !partial, !stateEmpty, fetchEmpty	-> D return groups in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge groups in state newer than those in action.groups
		 */
		case Actions.GROUP_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.groups.length === 0;
			const fetched = action.groups.map(group => new Group({...group, persisted: true}));

			if (logging) console.debug(`⮂ groupReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} groups with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ groupReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ groupReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Group> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Group[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(group => {
					const current = st.get(group.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && group.status === 'D') {
							// Remove it from state
							toUpdate.push(group);
							st.delete(group);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (group.addedOn > current.addedOn) {
								toUpdate.push(group);
								st.set(group);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (group.status !== 'D') {
							toUpdate.push(group);
							st.set(group);
						}
						else if (logging) console.debug('⮂ groupReducer: partialMerge: skipping deleted group not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ groupReducer: empty: groups', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ groupReducer: fullMerge: groups', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localGroup => {
                    // If this group has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localGroup.persisted) {
                        const fetchedGroup = st.get(localGroup.id);
                        if (!fetchedGroup || localGroup.addedOn > fetchedGroup.addedOn) st.set(localGroup);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.GROUP_FETCH_ONE_FAILURE:
			console.error('GROUP_FETCH_ONE_FAILURE', action.groupId, action.err);

			// No update to storage required
			return state;

		case Actions.GROUP_FETCH_FAILURE:
			console.error('GROUP_FETCH_FAILURE', action.groupId, action.err);

			// No update to storage required
			return state;

		case Actions.GROUP_RESTORE: {
		    let group = new Group({...action.group});
            st = state.clone();

            const existing = st.get(group.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (group.addedOn >= existing.addedOn) {
                    st.set(group);
                    return updateStorage(st, [group]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (group.status === 'D') return state;

                st.set(group);
                return updateStorage(st, [group]);
            }
        }
            
		case Actions.GROUP_SAVE: {
		    // console.debug('⮂ GROUP_SAVE reducer', action.group?.id);
		    
			// clone the group
			let group = new Group({...action.group});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new group in the state
			// If this is a create, add to the queue
			const _group = st.get(group.id);

			if (!_group) st.set(group);
			else {
				if (group.addedOn >= _group.addedOn) st.set(group);
			}

			return updateStorage(st, [group]);
		}

		case Actions.GROUP_SAVE_MANY: {
		    if (logging) console.debug('⮂ GROUP_SAVE_MANY reducer', action.groups.map(group => group.id).join(','));
		    
			// clone the groups
			let toProcess = action.groups.map(group => new Group(group));

			st = state.clone();
			// state.clear();

			const updates: Group[] = [];

			toProcess.forEach(group => {
				// Add or update the new group in the state
				// If this is a create, add to the queue
				const existing = st.get(group.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (group.status !== 'D') {
						st.push(group);
						updates.push(group);
					}
					else if (logging) console.debug('⮂ GROUP_SAVE_MANY: skipping deleted group not in state');
				}
				else {
					if (group.addedOn >= existing.addedOn) {
						st.set(group);
						updates.push(group);
					}
					else if (logging) console.debug('⮂ Skipping, existing group had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.GROUP_REMOVE: {
			// clone the group
			const group = new Group({...action.group});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(group);

			return updateStorage(st, [group]);
		}

		case Actions.GROUP_RESTORE_FAILURE:
		case Actions.GROUP_SAVE_FAILURE: {
		    console.warn('GROUP_SAVE_FAILURE reducer', action.group?.id);
		    if (!action.group?.id) {
				console.warn('GROUP_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const group = ensure(Group, action.group);
			if (!group) {
				console.error('Could not find group after call to save');
				return state;
			}

			st = state.clone();

			const _groupRef = state.get(group.id);

			if (_groupRef && (_groupRef.addedOn === group.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _group = new Group({..._groupRef});
				_group.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_group.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('group', _group).then(r => {
						if (r.isErr()) console.error(`⮂ groupReducer: failed to save group with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ groupReducer: saved group with fatalError info');
					}).catch(err => console.error(`⮂ groupReducer: failed to save group with fatalError info: ${err.message}`));

				st.set(_group);
			}

			return updateStorage(st);
		}

		case Actions.GROUP_SAVE_MANY_FAILURE: {
		    console.warn('GROUP_SAVE_MANY_FAILURE reducer', action.groups.map(group => group.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.GROUP_RESTORE_SUCCESS:
		case Actions.GROUP_SAVE_SUCCESS: {
			if (!action.group?.id) {
				console.warn('GROUP_SAVE_SUCCESS reducer');
                return state;
			}

			const groupRef = state.get(action.group?.id);
			if (!groupRef) {
				console.warn('Could not find group after call to save');
                return state;
			}

			st = state.clone();

			if (action.group.addedOn >= groupRef.addedOn) {
				st.set(action.group);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const group = new Group({...groupRef, persisted: true});
				st.set(group);
			}

			return updateStorage(st, [action.group]);
		}

		case Actions.GROUP_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ GROUP_SAVE_MANY_SUCCESS reducer', action.groups.map(group => group.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const groups: Group[] = [];

			action.groups.forEach(e => {
				const groupRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!groupRef) console.warn('Could not find group after call to save');
				else if (e.addedOn >= groupRef.addedOn) {
				    st.set(e);
				    groups.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const group = new Group({...groupRef, persisted: true});
					st.set(group);
					groups.push(group);
				}
			});

			return updateStorage(st, groups);
		}

		case Actions.GROUP_REMOVE_FAILURE: {
		    console.warn('GROUP_REMOVE_FAILURE action', action.group?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.GROUP_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.GROUP_RESTORE_MANY: {
			if (logging) console.debug('⮂ GROUP_RESTORE_MANY reducer', action.groups.map(group => group.id).join(','));

			const toProcess = action.groups.map(group => new Group({...group}));
			st = state.clone();

			const updates: Group[] = [];

			toProcess.forEach(group => {
				const existing = st.get(group.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (group.addedOn >= existing.addedOn) {
						st.set(group);
						updates.push(group);
					}
					else if (logging) console.debug('⮂ GROUP_RESTORE_MANY: skipping older version of group');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (group.status !== 'D') {
						st.set(group);
						updates.push(group);
					}
					else if (logging) console.debug('⮂ GROUP_RESTORE_MANY: skipping deleted group not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.GROUP_REMOVE_MANY: {
			if (logging) console.debug('⮂ GROUP_REMOVE_MANY reducer', action.groups.map(group => group.id).join(','));

			const toProcess = action.groups.map(group => new Group({...group}));
			st = state.clone();

			toProcess.forEach(group => {
				st.delete(group);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default groups;